<script>
import moment from "moment";

/**
 * @typedef {object} GanttData
 * @property {string} label
 * @property {string} begin
 * @property {string} finish
 */

const today = moment();

const ChartColorsEnum = Object.freeze({
  darkestBlue: "#0e2841",
  darkBlue: "#104862",
  smoke_90: "#364d6e",
  darkBlue_25: "#215f9a",
  smoke_80: "#4c6c9c",
  smoke_60: "#6f8db9",
  smoke_40: "#9db1cf",
  smoke_20: "#c3cfe1",
  smoke_10: "#dfe5ef",
});

import { UserTypeEnum } from "@/core/enums/user-types";
import { mapGetters } from "vuex";

export default {
  props: {
    /** @type {GanttData[]} */
    data: {
      type: Array,
      default: () => ([])
    },
    year: {
      type: Number,
      default: () => today.get("year")
    }
  },
  computed: {
    ...mapGetters(["user"]),
    headerColor() {
      const user = this.user || {};
      const { tipo_usuario } = user;
      const isClient = [UserTypeEnum.GERENCIAL_CLIENTE, UserTypeEnum.COLABORADOR].includes(tipo_usuario);
      return isClient ? 'rgba(11, 84, 86, 0.9)' : '#1c1c44';
    },
    headerStyle() {
      return {
        backgroundColor: this.headerColor
      };
    },
    cols() {
      return [
        "Macro etapa",
        "Atividades",
        "jan",
        "fev",
        "mar",
        "abr",
        "mai",
        "jun",
        "jul",
        "ago",
        "set",
        "out",
        "nov",
        "dez",
      ];
    },
    rows() {
      const colors = [
        ChartColorsEnum.darkestBlue,
        ChartColorsEnum.darkBlue,
        ChartColorsEnum.darkBlue_25
      ];
      
      const yearStart = moment(`${this.year}-01-01`);
      const yearEnd = moment(`${this.year}-12-31`);
      const totalDays = yearEnd.diff(yearStart, 'days') + 1;

      return this.data.map(({ me, atividades }, index) => ({
        me,
        atividades: atividades.map(a => {
          
          if (!a.data_inicio || !a.data_fim) {
            return {
              ...a,
              barStyle: { display: 'none' },
              formattedDates: ''
            };
          }

          const start = moment(a.data_inicio);
          const end = moment(a.data_fim);
          
          let diffDaysStart = start.diff(yearStart, 'days');
          let durationDays = end.diff(start, 'days') + 1;

          let left = (diffDaysStart / totalDays) * 100;
          let width = (durationDays / totalDays) * 100;
          
          if (left < 0) {
             width += left; 
             left = 0;
          }
          if (width < 0) width = 0;
          if (left + width > 100) width = 100 - left;
          
            let labelStyle = {
              backgroundColor: colors[index % colors.length],
              width: 'max-content'
            };

            const centerPosition = left + (width / 2);
            if (centerPosition > 90) {
              labelStyle = {
                ...labelStyle,
                left: 'auto',
                right: '0',
                transform: 'translate(0, -50%)' 
              };
            }

            return {
              ...a,
              barStyle: {
                left: `${left}%`,
                width: `${width}%`,
                backgroundColor: colors[index % colors.length]
              },
              labelStyle,
              tooltipDates: `${start.format("DD/MM/YYYY")} - ${end.format("DD/MM/YYYY")}`,
              displayDates: end.diff(start, 'months', true) <= 2
                ? `${start.format("DD/MM/YYYY")}<br/>${end.format("DD/MM/YYYY")}`
                : `${start.format("DD/MM/YYYY")} - ${end.format("DD/MM/YYYY")}`
            };
        }),
      }));
    }
  },
  mounted() {
    // console.log('Gantt Data:', this.data);
  }
};
</script>

<template>
  <v-card class="gantt-chart">
    <div class="gantt-headers" :style="headerStyle">
      <div v-for="(col, index) of cols" :key="index" class="gantt-header">
        {{ col }}
      </div>
    </div>

    <div class="gantt-body">
      <div v-for="(group, groupI) of rows" :key="groupI" class="gantt-group">
        
        <!-- Macro Etapa Column -->
        <div class="gantt-cell me-cell">
          <span class="font-weight-bold">{{ group.me }}</span>
        </div>

        <!-- Activities Container -->
        <div class="activities-container">
          <div v-for="(atividade, atvI) in group.atividades" :key="atvI" class="gantt-row">
            
            <!-- Activity Column -->
            <div class="gantt-cell atv-cell">
              <span>{{ atividade.nome }}</span>
            </div>

            <!-- Timeline Columns -->
            <div class="gantt-timeline">
              <div v-for="m in 12" :key="m" class="gantt-cell month-cell"></div>
              
              <!-- Activity Bar -->
              <div 
                v-if="atividade.barStyle && parseFloat(atividade.barStyle.width) > 0"
                class="gantt-bar" 
                :style="atividade.barStyle"
                :title="atividade.tooltipDates"
              >
                <div 
                  class="gantt-bar-label" 
                  v-html="atividade.displayDates"
                  :style="atividade.labelStyle"
                ></div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<style scoped lang="scss">
.gantt-chart {
  overflow-x: auto; 
  width: 100%;

  .gantt-headers {
    display: flex;
    width: 100%;

    .gantt-header {
      text-align: center;
      flex: 1; // Distribute remaining space equally
      padding: 0.5rem;
      font-weight: bold;
      color: white;
      border-right: 1px solid rgba(255,255,255,0.2);
      min-width: 40px; // Minimum width to prevent collapse

      &:nth-child(1) { flex: 0 0 200px; text-align: left; } // ME - fixed width
      &:nth-child(2) { flex: 0 0 150px; text-align: left; } // Atv - fixed width
    }
  }

  .gantt-body {
    background-color: #fff;
    width: 100%;
  }

  .gantt-group {
    display: flex;
    border-bottom: 1px solid #e0e0e0;
  }

  .activities-container {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  .gantt-row {
    display: flex;
    border-bottom: 1px solid #f0f0f0;
    
    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: #f5f5f5;
    }
  }

  .gantt-cell {
    padding: 0.5rem;
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    
    &.me-cell {
      flex: 0 0 200px;
      border-right: 1px solid #e0e0e0;
      background-color: #fafafa;
    }

    &.atv-cell {
      flex: 0 0 150px;
      border-right: 1px solid #e0e0e0;
    }

    &.month-cell {
      flex: 1; // Flexible width
      border-right: 1px solid #f0f0f0;
      height: 100%;
      min-height: 40px;
    }
  }

  .gantt-timeline {
    display: flex;
    position: relative;
    flex: 1; // Take remaining space
  }

  .gantt-bar {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    height: 34px;
    border-radius: 4px;
    color: white;
    font-size: 0.75rem;
    white-space: nowrap;
    overflow: visible !important; 
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    z-index: 10; 
    
    .gantt-bar-label {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      color: #fff;
      padding: 2px 6px;
      border-radius: 4px;
      pointer-events: none;
      z-index: 20;
      text-align: center;
      line-height: 1;
      white-space: normal;
    }
  }
}
</style>